import React, { useContext, useEffect, useState } from 'react';
import { Grid, Stack, TextField, LinearProgress, Box } from '@mui/material';
import { KioskContext } from '../../context/KioskProviderContext';
import { CHROME, WINDOWS } from '../../scripts/Platform';
import { useTranslation } from 'react-i18next';
import { verifyConnection } from '../../scripts/check-connectivity/CheckConnectivity';
import { STORAGE_VERIFY_CONNECTION } from './Storage';
import { getFromStorage } from '../../scripts/common/StorageHelper';

const About = ({
  triggerCheck,
  resetCheckTrigger,
  isVerifying,
  setIsVerifying,
}) => {
  const {
    platform,
    displayMode,
    isSecure,
    installedExtVersion,
    canActivateWakeLock,
    canOpenNewWindow,
    errorLog,
    takeATestDetails,
  } = useContext(KioskContext);
  const { t } = useTranslation();
  const [connectionStatus, setConnectionStatus] = useState(undefined);

  useEffect(() => {
    // Load connection status from session storage
    const status = getFromStorage(STORAGE_VERIFY_CONNECTION);
    if (status) {
      setConnectionStatus(status);
    }
  }, []);

  useEffect(() => {
    if (triggerCheck) {
      setIsVerifying(true);
      verifyConnection(triggerCheck)
        .then((status) => {
          setConnectionStatus(status);
          resetCheckTrigger();
        })
        .finally(() => {
          setIsVerifying(false);
        });
    }
  }, [triggerCheck, resetCheckTrigger, setIsVerifying]);

  return (
    <Stack spacing={2}>
      <Grid container>
        <Grid item xs={8}>
          {t('components.common.applicationVersion')}
        </Grid>
        <Grid item xs={4}>
          <i>v{process.env.REACT_APP_VERSION}</i>
        </Grid>
        <Grid item xs={8}>
          {t('components.common.buildNumber')}
        </Grid>
        <Grid item xs={4}>
          <i>b{process.env.REACT_APP_BUILD_VERSION}</i>
        </Grid>
        {platform === CHROME && (
          <>
            <Grid item xs={8}>
              {t('components.common.chromeExtensionVersionExpected')}
            </Grid>
            <Grid item xs={4}>
              <i>v{window.env.REACT_APP_EXT_VERSION}</i>
            </Grid>
            <Grid item xs={8}>
              {t('components.common.installedChromeExtensionVersion')}
            </Grid>
            <Grid item xs={4}>
              {installedExtVersion && <i>v{installedExtVersion}</i>}
              {!installedExtVersion && <i>n/a</i>}
            </Grid>
          </>
        )}
        <Grid item xs={8}>
          {t('components.common.platform')}
        </Grid>
        <Grid item xs={4}>
          {platform}
        </Grid>
        <Grid item xs={8}>
          {t('components.common.display')}
        </Grid>
        <Grid item xs={4}>
          {displayMode}
        </Grid>
        <Grid item xs={8}>
          {t('components.common.kiosk')}
        </Grid>
        <Grid item xs={4}>
          {isSecure ? t('common.yes') : t('common.no')}
        </Grid>
        {platform === WINDOWS && (
          <>
            {canActivateWakeLock !== undefined && (
              <>
                <Grid item xs={8}>
                  {t('components.common.wakeLockSupport')}
                </Grid>
                <Grid item xs={4}>
                  {canActivateWakeLock ? t('common.yes') : t('common.no')}
                </Grid>
              </>
            )}
            {canOpenNewWindow !== undefined && (
              <>
                <Grid item xs={8}>
                  {t('components.common.newWindow')}
                </Grid>
                <Grid item xs={4}>
                  {canOpenNewWindow ? t('common.yes') : t('common.no')}
                </Grid>
              </>
            )}
            {takeATestDetails !== undefined &&
              !takeATestDetails.isNotSecureBrowser && (
                <>
                  <Grid item xs={8}>
                    {t('components.common.takeSecureTest')}
                  </Grid>
                  <Grid item xs={4}>
                    {`${takeATestDetails.secure === true ? t('common.yes') : t('common.no')} ${takeATestDetails.view ? `(${takeATestDetails.view})` : ''}`}
                  </Grid>
                </>
              )}
          </>
        )}
      </Grid>
      {window.env.REACT_APP_DEBUG && !isSecure && errorLog && (
        <TextField
          multiline
          fullWidth
          value={`Kiosk check logs: \n${errorLog}`}
          InputProps={{
            readOnly: true,
          }}
        />
      )}
      {(isVerifying || connectionStatus !== undefined) && (
        <Box sx={{ mt: 2, position: 'relative' }}>
          {isVerifying && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
              }}
            />
          )}
          <TextField
            multiline
            fullWidth
            minRows={8}
            value={
              isVerifying
                ? 'Verifying connections...'
                : `Connection Status:\n${Object.entries(connectionStatus)
                    .map(([key, value]) => {
                      const status = value.ok ? 'Ok' : 'Unable to connect';
                      return `${value.label || key} ${status}`;
                    })
                    .join('\n')}`
            }
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default About;
